import classNames from 'classnames';
import React, { FC, useContext } from 'react';
// eslint-disable-next-line import/no-cycle
import ResourceContext, { Tag } from '@/components/Resources/context';
import PC from '@/components/Device/PC';
import Mobile from '@/components/Device/Mobile';
import useIsMobile from '@/hooks/useIsMobile';
import { traceEvent } from '@wk/wk-gatherer';
import router from 'next/router';

const containCurrentValue = (tags: Tag[], currentTag: Tag) => {
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < tags.length; i++) {
    const item = tags[i];
    if (item.value === currentTag.value && item.parentValue === currentTag.parentValue) {
      return true;
    }
  }

  return false;
};

// 判断移动端该父级下是否具有子集标签，如果有则父级标红
const containCurrentMobileValue = (mobileTags: Tag[], currentTags: Tag[]) => {
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < mobileTags.length; i++) {
    const item = mobileTags[i];
    if (currentTags.some((tag) => item.value === tag.value)) {
      return true;
    }
  }

  return false;
};

interface Props {
  label: string;
  value: string;
  subMenu: Tag[];
  mobileTags: Tag[];
  pushMobileTag: (tag: Tag) => void;
  deleteMobileTag: (tag: Tag) => void;
  openMenuKeys: string[];
  changeOpen: (key: string) => void;
}

const MenuItem: FC<Props> = ({
  label: parentLabel,
  value: parentKey,
  subMenu,
  mobileTags,
  pushMobileTag,
  deleteMobileTag,
  openMenuKeys,
  changeOpen,
}) => {
  const { filterTags, pushFilterTagAndUpdatePath, deleteFilterTagAndUpdatePath } = useContext(ResourceContext);
  const isOpen = openMenuKeys?.includes?.(parentKey);
  const isMobile = useIsMobile();

  return (
    <>
      <div
        onClick={() => changeOpen(parentKey)}
        className={classNames(
          'flex cursor-pointer items-center justify-between font-bold',
          containCurrentMobileValue(mobileTags, subMenu) && isMobile ? 'text-[#FF5353]' : '',
        )}
      >
        <span>{parentLabel}</span>
        <svg
          className={classNames(isOpen ? 'rotate-180' : '', 'transition-transform duration-300')}
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M11.9824 6.85715L8.34587 10.4937C8.12272 10.7169 7.76091 10.7169 7.53775 10.4937L3.9012 6.85715"
            stroke="#484C70"
            strokeWidth="1.71429"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <li className="relative mb-5 border-b-[1px] border-solid border-[#EBECFA] font-semibold text-[#1B1F4D] last:mb-0 last:border-b-0">
        <ul
          className={classNames('overflow-hidden pt-5 transition-[height] duration-300', isOpen && 'pb-1')}
          style={{ height: isOpen ? 'auto' : 0 }}
        >
          <PC>
            {subMenu.map((item) => (
              <li
                onClick={() => {
                  traceEvent({
                    data: {
                      _event: 'O_LearningWorldList_MenuClick',
                      subject: router?.query?.subject,
                      options: item.label,
                    },
                  });
                  pushFilterTagAndUpdatePath(item);
                }}
                key={item.value}
                className={classNames(
                  containCurrentValue(filterTags, item) ? 'text-[#FF5353]' : '',
                  'mb-[16px] flex cursor-pointer justify-between text-[14px] font-normal text-[#484C70]',
                )}
              >
                <span className="line-clamp-1 w-[201px] hover:text-[#FF5353]">{item.label}</span>
                {containCurrentValue(filterTags, item) && (
                  <svg
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteFilterTagAndUpdatePath(item);
                    }}
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="10" cy="10" r="10" fill="#F5F5FC" />
                    <path
                      d="M6.82031 6.81348L13.1843 13.1774"
                      stroke="#8D8FA6"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M13.1797 6.81348L6.81573 13.1774"
                      stroke="#8D8FA6"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
              </li>
            ))}
          </PC>
          <Mobile>
            {subMenu.map((item) => (
              <li
                onClick={() => {
                  traceEvent({
                    data: {
                      _event: 'O_LearningWorldList_MenuClick',
                      subject: router?.query?.subject,
                      options: item.label,
                    },
                  });
                  pushMobileTag(item);
                }}
                key={item.value}
                className={classNames(
                  containCurrentValue(mobileTags, item) ? 'text-[#FF5353]' : '',
                  'mb-[16px] line-clamp-1 flex cursor-pointer justify-between text-[14px] font-normal text-[#484C70]',
                )}
              >
                <span className="w-[201px]">{item.label}</span>
                {containCurrentValue(mobileTags, item) && (
                  <svg
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteMobileTag(item);
                    }}
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-[20px] w-[25px]"
                  >
                    <circle cx="10" cy="10" r="10" transform="matrix(-1 0 0 1 20 0)" fill="#F5F5FC" />
                    <path
                      d="M13.1797 6.8125L6.81573 13.1765"
                      stroke="#8D8FA6"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                    <path
                      d="M6.82031 6.8125L13.1843 13.1765"
                      stroke="#8D8FA6"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                  </svg>
                )}
              </li>
            ))}
          </Mobile>
        </ul>
      </li>
    </>
  );
};

export default MenuItem;
